<template>
    <div class="modal-open themeBgModalOverlay fixed inset-x-0 top-0 z-50 grid h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full"
        tabindex="0">
        <div class="self-center justify-self-center relative w-full md:px-4 py-10"
            :class="widthClass ? widthClass : 'max-w-7xl'">
            <div class="relative rounded-lg hrm_modal_container">
                <div class="flex items-center justify-between p-2  md:p-4 rounded-t hrm_modal_header">
                    <h3
                        class=" text-sm md:text-base font-semibold text-gray-800 dark:text-gray-300 flex gap-2 items-center">
                        <template v-if="modalData?.headerIcon">
                            <Icon :icon="modalData?.headerIcon" width="1.2em" height="1.2em" />
                        </template>
                        {{ modalData.title }}
                    </h3>
                    <button type="button"
                        class="hrm_modal_close_button themeModalClose w-7 h-7 flex items-center justify-center"
                        @click="close()">
                        <Icon icon="maki:cross" width="1.2em" height="1.2em" />
                    </button>
                </div>
                <div class="hrm_modal_content">
                    <component ref="modalComponent" :is="currentComponent" :modalData="modalData" @close="close"
                        @submitDelete="submitDelete" @refresh="refresh" :processingData="processingData"
                        @refreshOnComponentAdd="refreshTable" />
                </div>
                <div class="flex items-center justify-end gap-2 md:px-4 py-4 hrm_modal_footer">
                    <template v-if="modalData.showButtons">
                        <template v-if="modalData.customButton">
                            <hrm-button v-if="modalData.isReject" @click="submitForm()" buttonMode="progressButton"
                                iconSize="22px" buttonPadding="10px 16px" :buttonClass="`flex justify-center items-center rounded-full hrm_submit_button ${modalData.status == 'rejected'
                                    ? 'themeButtonCancel'
                                    : ''
                                    }`" buttonAlign="center" icon="fluent:tasks-app-24-regular"
                                :title="modalData.buttonText || `Submit`" :disabled="processingData.process"
                                :loading="processingData.process" :style="modalData.status != 'rejected'
                                    ? `background : #038603`
                                    : ''
                                    " />
                        </template>
                        <template v-else-if="modalData.customButtons">
                            <hrm-button @click="submitForm('rejected')" buttonMode="progressButton" iconSize="22px"
                                buttonPadding="10px 16px"
                                buttonClass="flex justify-center items-center rounded-full hrm_button_cancel"
                                buttonAlign="center" icon="fluent:tasks-app-24-regular" title="Rejected"
                                :disabled="processingData.reject || processingData.approve"
                                :loading="processingData.reject" />
                            <hrm-button @click="submitForm('approved')" buttonMode="progressButton" iconSize="22px"
                                buttonPadding="10px 16px"
                                buttonClass="flex justify-center items-center rounded-full hrm_submit_button"
                                buttonAlign="center" icon="fluent:tasks-app-24-regular" title="Approved"
                                :disabled="processingData.approve || processingData.reject"
                                :loading="processingData.approve" />
                        </template>
                        <template v-else-if="modalData.buttonType">
                            <hrm-button @click="close()" buttonMode="textButton" iconSize="22px"
                                buttonPadding="10px 16px"
                                buttonClass="flex justify-center items-center rounded-full hrm_button_cancel"
                                buttonAlign="center" icon="fluent:tasks-app-24-regular" title="Close" />
                        </template>
                        <template v-else>
                            <hrm-button @click="close()" buttonMode="textButton" iconSize="22px"
                                buttonPadding="10px 16px"
                                buttonClass="flex justify-center items-center rounded-full hrm_button_cancel"
                                buttonAlign="center" icon="fluent:tasks-app-24-regular" title="Close" />
                            <hrm-button v-if="modalData.draftbutton" @click="savedraft('draft')"
                                buttonMode="progressButton" iconSize="18px" buttonPadding="10px 16px"
                                buttonClass="flex justify-center items-center rounded-full hrm_submit_button"
                                buttonAlign="center" icon="fluent:tasks-app-24-regular"
                                :title="modalData.draftbuttonText || `Draft`" :disabled="processingData.isProcessDraft"
                                :loading="processingData.isProcessDraft" />
                            <hrm-button v-if="modalData.submitButton" @click="submitForm('published')"
                                buttonMode="progressButton" iconSize="18px" buttonPadding="10px 16px"
                                buttonClass="flex justify-center items-center rounded-full hrm_submit_button"
                                buttonAlign="center" icon="fluent:tasks-app-24-regular"
                                :title="modalData.buttonText || `Submit`"
                                :disabled="processingData.process || processingData.disabledSubmit"
                                :loading="processingData.process" />
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineAsyncComponent, onMounted } from "vue";
import { TrashIcon, XCircleIcon } from "@heroicons/vue/24/solid";
import { defineProps, defineEmits } from "vue";
import { Icon } from "@iconify/vue";

// Use defineProps to access props
const props = defineProps();
const compName = ref(props.compName);
const modalData = ref(props.modalData);
const widthClass = ref(props.widthClass);

// Use defineEmits to access emit function
const emit = defineEmits();
const modalComponent = ref(null);
const processingData = ref({
    process: false,
    isProcessDraft: false,
    disabledSubmit: false,
});

// Define close method
const close = () => {
    emit("close");
};

const refresh = (form) => {
    emit("refresh", form);
};
const refreshTable = () => {
    emit('refreshOnComponentAdd')
}

const submitDelete = () => {
    emit("submitDelete");
};

const submitForm = (type) => {
    modalComponent.value.submitForm(type);
};
const savedraft = (type) => {
    modalComponent.value.savedraft(type);
};

const components = {
    CopyTokenKey: defineAsyncComponent(() =>
        import("../API/CopyKey.vue")
    ),
    //Delete
    DeleteModal: defineAsyncComponent(() =>
        import("../../Components/Common/HrmDelete.vue")
    ),
    // CoreHr
    CoreHrAwardAdd: defineAsyncComponent(() =>
        import("./../CoreHr/Awards/Add.vue")
    ),
    CoreHrTransfersAdd: defineAsyncComponent(() =>
        import("./../CoreHr/Transfers/Add.vue")
    ),
    CoreHrPromotionAdd: defineAsyncComponent(() =>
        import("./../CoreHr/Promotions/Add.vue")
    ),
    CoreHrTravelAdd: defineAsyncComponent(() =>
        import("./../CoreHr/Travels/Add.vue")
    ),
    CoreHrVerifyTravel: defineAsyncComponent(() =>
        import("./../CoreHr/Travels/Verify.vue")
    ),
    CoreHrComplaintAdd: defineAsyncComponent(() =>
        import("./../CoreHr/Complaints/Add.vue")
    ),
    CoreHrVerifyComplaint: defineAsyncComponent(() =>
        import("./../CoreHr/Complaints/VerifyComplaint.vue")
    ),
    CoreHrWarningAdd: defineAsyncComponent(() =>
        import("./../CoreHr/Warnings/Add.vue")
    ),
    CoreHrTerminationAdd: defineAsyncComponent(() =>
        import("./../CoreHr/Terminations/ChangeStatus.vue")
    ),
    CoreHrLoanAdd: defineAsyncComponent(() =>
        import("./../CoreHr/Loans/Add.vue")
    ),
    CoreHrLoanAddEmi: defineAsyncComponent(() =>
        import("./../CoreHr/Loans/AddEmi.vue")
    ),
    CoreHrLoanDetail: defineAsyncComponent(() =>
        import("./../CoreHr/Loans/Detail.vue")
    ),
    CoreHrLoanReject: defineAsyncComponent(() =>
        import("./../CoreHr/Loans/RejectLoan.vue")
    ),

    // Organization
    OrganizationCompanyAdd: defineAsyncComponent(() =>
        import("./../Organization/Companies/Add.vue")
    ),
    OrganizationLegalEntityAdd: defineAsyncComponent(() =>
        import(
            "./../../../../Modules/Payroll/Resources/js/Components/LegalEntity/Add.vue"
        )
    ),
    WorkSourceAdd: defineAsyncComponent(() =>
        import("../../../../Modules/QuoteManager/Resources/js/Components/WorkSource/Add.vue")
    ),
    OrganizationOfficialDocumentsAdd: defineAsyncComponent(() =>
        import("./../Organization/OfficialDocuments/Add.vue")
    ),
    OrganizationLocationAdd: defineAsyncComponent(() =>
        import("./../Organization/Location/Add.vue")
    ),
    OrganizationDepartmentAdd: defineAsyncComponent(() =>
        import("./../Organization/department/Add.vue")
    ),
    OrganizationDesignationAdd: defineAsyncComponent(() =>
        import("./../Organization/designation/Add.vue")
    ),
    OrganizationAnnouncementAdd: defineAsyncComponent(() =>
        import("./../Organization/announcement/Add.vue")
    ),
    OrganizationCompanyPolicyAdd: defineAsyncComponent(() =>
        import("./../Organization/companyPolicy/Add.vue")
    ),

    // Subscription
    SubscriptionPlanAdd: defineAsyncComponent(() =>
        import("./../../Components/Plans/Add.vue")
    ),

    // Document Builder
    DocumentBuilderPreview: defineAsyncComponent(() =>
        import("./../../../../Modules/Document/Resources/js/Components/DocumentFormat/Preview.vue")
    ),
    EmployeeDocumentPreview: defineAsyncComponent(() =>
        import("../../../../Modules/EmployeeManagement/Resources/js/Components/Employee/Documents/Preview.vue")
    ),

    // Separation
    SeparationProcessEmployeeTask: defineAsyncComponent(() =>
        import(
            "./../../../../Modules/Separation/Resources/js/Pages/EmployeeTask/Add.vue"
        )
    ),

    SeparationProcessImportTask: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Separation/Resources/js/Pages/EmployeeTask/Import.vue"
        )
    ),

    //common
    SeparationAdd: defineAsyncComponent(() =>
        import("./../Separation/Add.vue")
    ),

    'FlowJobOffer': defineAsyncComponent(() =>
        import("../../../../Modules/Recruitment/Resources/js/Components/JobPost/Offer.vue")
    ),

    'ManageEmployeeTask': defineAsyncComponent(() =>
        import("../../../../Modules/Recruitment/Resources/js/Components/JobPost/ManageEmployeeTask.vue")
    ),
    'AddEmployeeTask': defineAsyncComponent(() =>
        import("../../../../Modules/Recruitment/Resources/js/Components/JobPost/AddEmployeeTask.vue")
    ),
    'ManageEmployeeTasks': defineAsyncComponent(() =>
        import("../../../../Modules/Recruitment/Resources/js/Components/JobPost/ManageEmployeeTasks.vue")
    ),

    //Constant Pages
    GlobalConstant: defineAsyncComponent(() =>
        import("./../System/Constants/Add.vue")
    ),

    AddInvoicePayment: defineAsyncComponent(() =>
        import("../../../../Modules/QuoteManager/Resources/js/Components/InvoiceRefunds/Add.vue")
    ),

    AddInvoiceRefund: defineAsyncComponent(() =>
        import("../../../../Modules/QuoteManager/Resources/js/Components/InvoicePayments/Add.vue")
    ),
    AutoInvoiceGenerate: defineAsyncComponent(() =>
        import(
            "../../../../Modules/QuoteManager/Resources/js/Components/Invoices/Generate.vue"
        )
    ),
    AdvanceSalaryHistory: defineAsyncComponent(() =>
        import(
            "../../../../Modules/EmployeeManagement/Resources/js/Components/Employee/Salary/AdvanceSalary/AdvanceSalaryHistory.vue"
        )
    ),


    TagsAdd: defineAsyncComponent(() => import("./../Tags/Add.vue")),

    TenantAddAgent: defineAsyncComponent(() => import("./../Agent/Add.vue")),

    RoleAdd: defineAsyncComponent(() => import("./../../Pages/Role/Add.vue")),

    //Tenant All modals
    ResetTenant: defineAsyncComponent(() =>
        import("../Tenant/ResetTenant.vue")
    ),
    ResetPassword: defineAsyncComponent(() =>
        import("../Tenant/ResetPassword.vue")
    ),
    AddTenant: defineAsyncComponent(() => import("../Tenant/Add.vue")),
    EditEmail: defineAsyncComponent(() => import("../Tenant/Edit.vue")),

    //Tenant All modals
    AddLead: defineAsyncComponent(() => import("../Lead/Add.vue")),
    ConvertLead: defineAsyncComponent(() => import("../Tenant/Add.vue")),

    ConstantwidgetCategoryAdd: defineAsyncComponent(() =>
        import("../WidgetCategory/Add.vue")
    ),
    ConstantcompanyCategoryAdd: defineAsyncComponent(() =>
        import("../CoreHr/company/CompanyCategory.vue")
    ),

    //Employee
    StaffEmployeeAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/EmployeeManagement/Resources/js/Components/Employee/Add.vue"
        )
    ),

    //Reset Password
    ResetUserPass: defineAsyncComponent(() =>
        import("../../Pages/Profile/Partials/UpdatePasswordForm.vue")
    ),
    // Timesheet -- Holiday
    TimesheetHolidayAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Timesheet/Resources/js/Components/Holidays/Add.vue"
        )
    ),
    TimesheetOverTimeRequestAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Timesheet/Resources/js/Components/OverTimeRequest/Add.vue"
        )
    ),
    TimesheetOfficeOffs: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Timesheet/Resources/js/Components/OfficeShift/WeekOffs.vue"
        )
    ),
    TimesheetOfficeShiftAddNew: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Timesheet/Resources/js/Components/OfficeShift/AddShift.vue"
        )
    ),
    TimesheetLeavesAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Timesheet/Resources/js/Components/Leaves/Add.vue"
        )
    ),
    TimesheetLeaveVerify: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Timesheet/Resources/js/Components/Leaves/Verify.vue"
        )
    ),
    AdvanceSalaryVerify: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Payroll/Resources/js/Components/AdvanceSalary/Verify.vue"
        )
    ),
    AddProjectCredentialMembers: defineAsyncComponent(() =>
        import("../../../../Modules/QuoteManager/Resources/js/Components/Clients/ProjectCredentialMembers.vue")
    ),


    //Calling Data Center
    CallingUniversityAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/DataCenter/Resources/js/Components/University/Add.vue"
        )
    ),
    CallingCandidateAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/DataCenter/Resources/js/Components/Candidate/Add.vue"
        )
    ),
    CallingDataProviderAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/DataCenter/Resources/js/Components/DataProvider/DataProvider.vue"
        )
    ),
    CallingTeamModalAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/DataCenter/Resources/js/Components/DataProvider/TeamModalData.vue"
        )
    ),
    DataProviderStreamAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/DataCenter/Resources/js/Components/DataProvider/DataProviderStreamAdd.vue"
        )
    ),
    CallingDataAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/DataCenter/Resources/js/Components/DataProviderType/Add.vue"
        )
    ),

    //  payroll
    PayrollPayGroupAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Payroll/Resources/js/Components/PayGroup/Add.vue"
        )
    ),
    PayrollGenerate: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Payroll/Resources/js/Components/Payroll/Generate.vue"
        )
    ),

    PayrollGeneratePreview: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Payroll/Resources/js/Components/Payroll/PayrollGeneratePreview.vue"
        )
    ),

    PayrollPay: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Payroll/Resources/js/Components/Payroll/Pay.vue"
        )
    ),

    // Project manager
    ProjectManagerProjectAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/ProjectManagement/Resources/js/Components/Project/Add.vue"
        )
    ),
    ProjectManagerTaxTypeAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/QuoteManager/Resources/js/Components/TaxType/Add.vue"
        )
    ),
    TaskLogAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/ProjectManagement/Resources/js/Components/Project/AddTimeLog.vue"
        )
    ),
    ProjectTimeLogAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/ProjectManagement/Resources/js/Components/Project/ProjectTimeLog.vue"
        )
    ),

    AddDateWiseBilling: defineAsyncComponent(() =>
        import(
            "../../../../Modules/ProjectManagement/Resources/js/Components/Project/AddDateWiseBilling.vue"
        )
    ),

    // Quote manager
    QuoteManagerClientAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/QuoteManager/Resources/js/Components/Clients/Add.vue"
        )
    ),

    QuoteManagerWorkProfileExpenseAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/QuoteManager/Resources/js/Components/WorkProfileExpense/Add.vue"
        )
    ),

    // Finance Module
    FinanceGroupAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Finance/Resources/js/Components/Group/Add.vue"
        )
    ),
    FinanceFinancialYearAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Finance/Resources/js/Components/FinancialYear/Add.vue"
        )
    ),
    FinanceAccountAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Finance/Resources/js/Components/Accounts/Add.vue"
        )
    ),
    FinanceCompanyAccountAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Finance/Resources/js/Components/BankAccount/Add.vue"
        )
    ),
    FinanceBankDebitMethodAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Finance/Resources/js/Components/BankDebitMethod/Add.vue"
        )
    ),

    //  Tickets
    TicketAdd: defineAsyncComponent(() =>
        import("../../../../Modules/Ticket/Resources/js/Components/Add.vue")
    ),

    // Recruitment module
    RecruitmentRequisitionAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Recruitment/Resources/js/Components/Requisition/Add.vue"
        )
    ),

    // Assets
    AssetAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Inventory/Resources/js/Components/Assets/Add.vue"
        )
    ),
    AssetCodeIndex: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Inventory/Resources/js/Components/Assets/AssetCodeIndex.vue"
        )
    ),
    AssetCodeView: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Inventory/Resources/js/Components/Assets/AssetCodeView.vue"
        )
    ),
    AssetCategoryAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Inventory/Resources/js/Components/Category/Add.vue"
        )
    ),

    EventAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/EventManagement/Resources/js/Components/Event/Add.vue"
        )
    ),

    EventDetails: defineAsyncComponent(() =>
        import(
            "../../../../Modules/EventManagement/Resources/js/Components/Event/Details.vue"
        )
    ),
    MeetingDetails: defineAsyncComponent(() =>
        import(
            "../../../../Modules/EventManagement/Resources/js/Components/Meeting/Details.vue"
        )
    ),

    // Hosting module
    HostingServerAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Hosting/Resources/js/Components/Server/Add.vue"
        )
    ),
    HostingAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Hosting/Resources/js/Components/Hosting/Add.vue"
        )
    ),
    CommentPopUp: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Separation/Resources/js/Components/SeparationProcess/CommentPopUp.vue"
        )
    ),
    // Polls module
    PollsAdd: defineAsyncComponent(() =>
        import("../../../../Modules/Poll/Resources/js/Components/Polls/Add.vue")
    ),
    Result: defineAsyncComponent(() =>
        import("../../../../Modules/Poll/Resources/js/Pages/Polls/Result.vue")
    ),

    templateAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/EmployeeReport/Resources/js/Components/Templates/Add.vue"
        )
    ),
    StaffSeparationAdd: defineAsyncComponent(() =>
        import("../../../../resources/js/Components/Separation/Add.vue")
    ),
    RecruitmentCandidateAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/DataCenter/Resources/js/Components/Candidate/Add.vue"
        )
    ),
    JobScheduleDetails: defineAsyncComponent(() =>
        import("../../../../Modules/Recruitment/Resources/js/Components/Job/JobScheduleDetails.vue")
    ),

    JobCancelSchedule: defineAsyncComponent(() =>
        import("../../../../Modules/Recruitment/Resources/js/Components/Job/CancelScheduleJob.vue")
    ),

    meetingAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/EventManagement/Resources/js/Components/Meeting/Add.vue"
        )
    ),
    CandidateDetails: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Recruitment/Resources/js/Components/Job/CandidateDetails.vue"
        )
    ),
    CandidateOfferDetails: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Recruitment/Resources/js/Components/Job/CandidateOfferDetails.vue"
        )
    ),
    ImportPage: defineAsyncComponent(() =>
        import(
            "../../../../Modules/Import/Resources/js/Components/Import/ImportPage.vue"
        )
    ),

    // Flow
    FlowStageAdd: defineAsyncComponent(() => import("../../../../Modules/Flow/Resources/js/Components/Flow/Stage/Add.vue")),

    FlowFlowStagesAdd: defineAsyncComponent(() =>
        import("../../../../Modules/Flow/Resources/js/Components/Flow/FlowStages/Add.vue")
    ),

    AddFlowAction: defineAsyncComponent(() =>
        import("../../../../Modules/Flow/Resources/js/Components/Hiring/AddFlowAction.vue")
    ),

    AddNewFlowTask: defineAsyncComponent(() =>
        import("../../../../Modules/Flow/Resources/js/Components/Flow/FlowStages/AddNewFlowTask.vue")
    ),

    // custom field

    CustomFieldAdd: defineAsyncComponent(() =>
        import(
            "../../../../Modules/CustomField/Resources/js/Components/CustomField/Add.vue"
        )),

    RolesCollapse: defineAsyncComponent(() =>
        import("./../../Pages/Role/Add.vue")),



    DocumentPreview: defineAsyncComponent(() =>
        import("../../../../Modules/Document/Resources/js/Components/DocumentPreview.vue")),

    GroupTaxes: defineAsyncComponent(() =>
        import("../../../../Modules/QuoteManager/Resources/js/Components/GroupTaxes/Add.vue")),

    CommissionTypes: defineAsyncComponent(() =>
        import("../../../../Modules/QuoteManager/Resources/js/Components/CommissionType/Add.vue")),

    PaymentModesAdd: defineAsyncComponent(() =>
        import("./../../Components/System/Constants/PaymentModeAdd.vue")),

    LastLogInInfo: defineAsyncComponent(() =>
        import("../../../../Modules/EmployeeManagement/Resources/js/Components/EmployeeLastlogin/LastLogInInfo.vue")),

    AddChat: defineAsyncComponent(() =>
        import("../../../../Modules/Chat/Resources/js/Components/AddChat.vue")),

    ChangeEmployeeBulkStatus: defineAsyncComponent(() =>
        import("../../../../Modules/EmployeeManagement/Resources/js/Components/Employee/StatusUpdate.vue")),


    GenerateLoginCredentials: defineAsyncComponent(() =>
        import("../../../../Modules/EmployeeManagement/Resources/js/Components/Employee/GenerateLoginCredentials.vue")),

    CancelInvoiceStatus: defineAsyncComponent(() =>
        import("../../../../Modules/QuoteManager/Resources/js/Components/Invoices/StatusUpdate.vue")),
    EditNotificationTempalte: defineAsyncComponent(() =>
        import("./../Notification/Edit.vue")),

    AssetRecoveryDetail: defineAsyncComponent(() =>
        import('../../../../Modules/Separation/Resources/js/Components/SeparationProcess/AssetRecoveryDetail.vue')),

    cancelCommissionModal: defineAsyncComponent(() =>
        import('../../Components/Common/CancelCommission.vue')),

    AddNewFlowStage: defineAsyncComponent(() =>
        import('../../../../Modules/Flow/Resources/js/Components/Flow/Chart/AddStage.vue')),
    AddNewReminder: defineAsyncComponent(() =>
        import('../../Components/Reminder/Add.vue')),

    AddNewReport: defineAsyncComponent(() =>
        import('../../Components/Common/AddReport.vue')),
    ExportTableCols: defineAsyncComponent(() => import('../TablesComp/ExportTableCols.vue')),
    FinalStatementUpdate: defineAsyncComponent(() => import('../../../../Modules/Separation/Resources/js/Components/SeparationProcess/FinalStatementUpdate.vue')),
    TimesheetOverrides: defineAsyncComponent(() => import('../../../../Modules/Timesheet/Resources/js/Components/TimesheetOverrides/Add.vue')),

    ImportTemplateAdd: defineAsyncComponent(() => import('../../../../Modules/Import/Resources/js/Components/ImportTemplate/Add.vue'))
};


const currentComponent = ref(components[compName.value]);

const handleModalActions = () => {
    modalData.value.submitButton = modalData.value.submitButton != undefined
        ? modalData.value.submitButton
        : true;

    modalData.value.showButtons = modalData.value.showButtons != undefined
        ? modalData.value.showButtons
        : true;
}

onMounted(() => {
    handleModalActions();
})
</script>

<script>
// Define props and components for type inference
import { defineComponent } from "vue";
import { TrashIcon, XCircleIcon } from "@heroicons/vue/24/solid";

export default defineComponent({
    components: {
        TrashIcon,
        XCircleIcon,
    },
    props: {
        compName: {
            required: true,
            type: String,
        },
        modalData: {
            type: Object,
            default: () => ({
                submitButton: true,
                showButtons: true
            }),
        },
        widthClass: {
            type: String,
        },
    },
});
</script>
<style>
/* Hrm Modal buttons design */
.hrm_modal_container .hrm_button_cancel {
    background-color: #ff6a6a !important;
    color: #fff !important;
    transition: 0.4s;
    font-weight: 800;
    border-radius: 4px;
}

.hrm_modal_container .hrm_button_cancel:hover {
    background-color: #b64949 !important;
    opacity: 1;
}

.hrm_modal_container .hrm_submit_button {
    font-weight: 700;
    border-radius: 4px;
}

/* Fields design */
.hrm_modal_container .multiselect.hrm_input_field,
.hrm_modal_container .multiselect.hrm_input_field.is-active {
    background: transparent !important;
    border: none;
    box-shadow: none;
}

.hrm_modal_container .multiselect.hrm_input_field .multiselect-wrapper {
    height: 40px;
}

.hrm_modal_container .hrm_input input.hrm_input_field,
.hrm_modal_container .multiselect.hrm_input_field input,
.hrm_modal_container .dp__main.hrm_input_field input.dp__input {
    border-width: 2px !important;
    border-color: transparent !important;
    border-style: solid !important;
    border-radius: 4px !important;
}

.hrm_modal_container .hrm_input input.hrm_input_field:focus,
.hrm_modal_container .multiselect.hrm_input_field input:focus,
.hrm_modal_container .dp__main.hrm_input_field input.dp__input:focus,
.hrm_modal_container .hrm_input textarea.hrm_input_field:focus {
    border-width: 2px !important;
    border-color: var(--hrm_Theme_Bg_Color) !important;
}

.hrm_modal_container .hrm_input label.hrm_input_label {
    color: #dbdbdb !important;
    text-transform: capitalize;
}

.hrm_modal_container .hrm_input input.hrm_input_field::placeholder,
.hrm_modal_container .hrm_input .multiselect.hrm_input_field .multiselect-placeholder,
.hrm_modal_container .dp__main.hrm_input_field input.dp__input::placeholder {
    color: #757f8e !important;
}

.hrm_modal_container .hrm_input .hrm_input_error {
    color: #ff5a5a;
    font-size: 13px !important;
    font-weight: 600;
}

.hrm_modal_container .dp__main.hrm_input_field svg {
    padding: 0 !important;
    margin-left: 10px;
}
</style>
